import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LeadProvider } from "@/components/public/LeadDialog";
import { VirtualTourProvider } from "@/components/public/VirtualTour";
import OfferPopup from "@/components/public/OfferPopup";
import Tracker from "@/components/Tracker";
import CustomScripts from "@/components/CustomScripts";

import Home from "@/pages/public/Home";
import { WorkspacesList, LocationsList } from "@/pages/public/ListPages";
import WorkspaceDetail from "@/pages/public/WorkspaceDetail";
import LocationDetail from "@/pages/public/LocationDetail";
import PropertyOwners from "@/pages/public/PropertyOwners";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";
import TermsOfUse from "@/pages/public/TermsOfUse";
import Account from "@/pages/public/Account";
// Member area is lazy-loaded (not needed by public visitors).
const MemberLayout = lazy(() => import("@/components/member/MemberLayout"));
const MemberOverview = lazy(() => import("@/pages/member/Overview"));
const MyProfile = lazy(() => import("@/pages/member/MyProfile"));
const MyEnquiries = lazy(() => import("@/pages/member/MyEnquiries"));
const Membership = lazy(() => import("@/pages/member/Membership"));
const SavedSpaces = lazy(() => import("@/pages/member/SavedSpaces"));
const CommunicationHistory = lazy(() => import("@/pages/member/CommunicationHistory"));
const MemberSettings = lazy(() => import("@/pages/member/Settings"));
const ComingSoon = lazy(() => import("@/pages/member/ComingSoon"));
import { ContactPage, PricingPage, AmenitiesPage } from "@/pages/public/SimplePages";
import AboutUs from "@/pages/public/AboutUs";
import SeoLandingPage from "@/pages/public/SeoLandingPage";
const Blog = lazy(() => import("@/pages/public/Blog"));
const BlogCategory = lazy(() => import("@/pages/public/BlogCategory"));
const BlogPost = lazy(() => import("@/pages/public/BlogPost"));
const Careers = lazy(() => import("@/pages/public/Careers"));
const JobDetail = lazy(() => import("@/pages/public/JobDetail"));
const CareersApply = lazy(() => import("@/pages/public/CareersApply"));

import Login from "@/pages/admin/Login";
import AcceptInvite from "@/pages/admin/AcceptInvite";
// Entire admin panel is lazy-loaded so it never ships in the public bundle.
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Overview = lazy(() => import("@/pages/admin/Overview"));
const HeroEditor = lazy(() => import("@/pages/admin/HeroEditor"));
const CmsResource = lazy(() => import("@/pages/admin/CmsResource"));
const SettingsEditor = lazy(() => import("@/pages/admin/SettingsEditor"));
const Integrations = lazy(() => import("@/pages/admin/Integrations"));
const Leads = lazy(() => import("@/pages/admin/Leads"));
const AuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const OffersAdmin = lazy(() => import("@/pages/admin/OffersAdmin"));
const OfferLeads = lazy(() => import("@/pages/admin/OffersAdmin").then((m) => ({ default: m.OfferLeads })));
const PropertyOwnersAdmin = lazy(() => import("@/pages/admin/PropertyOwnersAdmin"));
const PrivacyPolicyAdmin = lazy(() => import("@/pages/admin/PrivacyPolicyAdmin"));
const TermsOfUseAdmin = lazy(() => import("@/pages/admin/TermsOfUseAdmin"));
const PropertyEnquiries = lazy(() => import("@/pages/admin/PropertyEnquiries"));
const WebsiteHub = lazy(() => import("@/pages/admin/WebsiteHub"));
const PageContentManager = lazy(() => import("@/pages/admin/PageContentManager"));
const SeoLandingPages = lazy(() => import("@/pages/admin/SeoLandingPages"));
const LandingPageEditor = lazy(() => import("@/pages/admin/LandingPageEditor"));
const BlogAdmin = lazy(() => import("@/pages/admin/BlogAdmin"));
const CareersDashboard = lazy(() => import("@/pages/admin/careers/CareersDashboard"));
const CareersJobs = lazy(() => import("@/pages/admin/careers/CareersJobs"));
const CareersJobForm = lazy(() => import("@/pages/admin/careers/CareersJobForm"));
const CareersApplications = lazy(() => import("@/pages/admin/careers/CareersApplications"));
const CareersSettings = lazy(() => import("@/pages/admin/careers/CareersSettings"));
const VisualEditorHub = lazy(() => import("@/pages/admin/VisualEditorHub"));
const VisualEditor = lazy(() => import("@/pages/admin/VisualEditor"));
const NavigationFooter = lazy(() => import("@/pages/admin/NavigationFooter"));
const SeoFooterManager = lazy(() => import("@/pages/admin/SeoFooterManager"));
const MembersAdmin = lazy(() => import("@/pages/admin/MembersAdmin"));
const RolesPermissions = lazy(() => import("@/pages/admin/RolesPermissions"));
const TeamAdmin = lazy(() => import("@/pages/admin/TeamAdmin"));
const AllUsers = lazy(() => import("@/pages/admin/AllUsers"));
const PropertyOwnersUsers = lazy(() => import("@/pages/admin/PropertyOwnersUsers"));
const FeatureControls = lazy(() => import("@/pages/admin/FeatureControls"));
const AnalyticsInsights = lazy(() => import("@/pages/admin/AnalyticsInsights"));
const Investigations = lazy(() => import("@/pages/admin/Investigations"));
const InvestigationDetail = lazy(() => import("@/pages/admin/InvestigationDetail"));
const LeadFormsAdmin = lazy(() => import("@/pages/admin/LeadFormsAdmin"));
const SalesModule = lazy(() => import("@/pages/admin/SalesModule"));
const KeywordRanks = lazy(() => import("@/pages/admin/KeywordRanks"));
const SeoManager = lazy(() => import("@/pages/admin/dm/SeoManager"));
const AeoSchema = lazy(() => import("@/pages/admin/dm/AeoSchema"));
const SitemapIndexing = lazy(() => import("@/pages/admin/dm/SitemapIndexing"));
const RedirectManager = lazy(() => import("@/pages/admin/dm/RedirectManager"));
const PageAnalytics = lazy(() => import("@/pages/admin/dm/PageAnalytics"));
const BrokenLinks = lazy(() => import("@/pages/admin/dm/BrokenLinks"));
const SearchPerformance = lazy(() => import("@/pages/admin/dm/SearchPerformance"));
const AnalyticsReports = lazy(() => import("@/pages/admin/dm/AnalyticsReports"));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <LeadProvider>
         <VirtualTourProvider>
          <Tracker />
          <CustomScripts />
          <OfferPopup />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/office-space" element={<WorkspacesList />} />
            <Route path="/workspaces" element={<Navigate to="/office-space" replace />} />
            <Route path="/office-space-old-2" element={<Navigate to="/office-space" replace />} />
            <Route path="/workspaces/:slug" element={<WorkspaceDetail />} />
            <Route path="/locations" element={<LocationsList />} />
            <Route path="/locations/:slug" element={<LocationDetail />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/amenities" element={<AmenitiesPage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/about" element={<Navigate to="/about-us" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/property-owners" element={<PropertyOwners />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/category/:slug" element={<BlogCategory />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:slug" element={<JobDetail />} />
            <Route path="/careers/:slug/apply" element={<CareersApply />} />
            <Route path="/account" element={<Account />} />
            <Route path="/dashboard" element={<Navigate to="/member" replace />} />
            <Route path="/member" element={<MemberLayout />}>
              <Route index element={<MemberOverview />} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="enquiries" element={<MyEnquiries />} />
              <Route path="membership" element={<Membership />} />
              <Route path="saved" element={<SavedSpaces />} />
              <Route path="communications" element={<CommunicationHistory />} />
              <Route path="settings" element={<MemberSettings />} />
              <Route path="community" element={<ComingSoon title="Community" subtitle="Connect with other CoWorkZen members." />} />
              <Route path="events" element={<ComingSoon title="Events" subtitle="Networking mixers and member events." />} />
              <Route path="support" element={<ComingSoon title="Support" subtitle="Get help from the CoWorkZen team." />} />
            </Route>

            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/accept-invite" element={<AcceptInvite />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Overview />} />
              <Route path="hero" element={<HeroEditor />} />
              <Route path="settings" element={<SettingsEditor />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="leads" element={<Leads />} />
              <Route path="offers" element={<OffersAdmin />} />
              <Route path="offers/:id/leads" element={<OfferLeads />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="cms/:resource" element={<CmsResource />} />
              <Route path="property-owners" element={<PropertyOwnersAdmin />} />
              <Route path="privacy-policy" element={<PrivacyPolicyAdmin />} />
              <Route path="terms-of-use" element={<TermsOfUseAdmin />} />
              <Route path="property-enquiries" element={<PropertyEnquiries />} />
              <Route path="website" element={<WebsiteHub />} />
              <Route path="page-content" element={<PageContentManager />} />
              <Route path="landing-pages" element={<SeoLandingPages />} />
              <Route path="landing-pages/new" element={<LandingPageEditor />} />
              <Route path="landing-pages/:id/edit" element={<LandingPageEditor />} />
              <Route path="blog" element={<BlogAdmin />} />
              <Route path="careers" element={<CareersDashboard />} />
              <Route path="careers/jobs" element={<CareersJobs />} />
              <Route path="careers/jobs/new" element={<CareersJobForm />} />
              <Route path="careers/jobs/:id/edit" element={<CareersJobForm />} />
              <Route path="careers/applications" element={<CareersApplications />} />
              <Route path="careers/settings" element={<CareersSettings />} />
              <Route path="visual-editor" element={<VisualEditorHub />} />
              <Route path="about-us" element={<Navigate to="/admin/visual-editor" replace />} />
              <Route path="analytics" element={<AnalyticsInsights />} />
              <Route path="investigations" element={<Investigations />} />
              <Route path="investigations/:key" element={<InvestigationDetail />} />
              <Route path="lead-forms" element={<LeadFormsAdmin />} />
              <Route path="sales" element={<SalesModule />} />
              <Route path="keywords" element={<KeywordRanks />} />
              <Route path="navigation" element={<NavigationFooter />} />
              <Route path="seo-footer" element={<SeoFooterManager />} />
              <Route path="workspace-enquiries" element={<Leads presetTypes={["tour", "quote", "space_enquiry"]} title="Workspace Enquiries" subtitle="Book a Tour, Get a Quote and workspace enquiries from the website." />} />
              <Route path="contact-requests" element={<Leads presetTypes={["general", "enterprise"]} title="Contact / Callback Requests" subtitle="General contact and enterprise callback requests." />} />
              <Route path="users" element={<AllUsers />} />
              <Route path="users/members" element={<MembersAdmin />} />
              <Route path="users/property-owners" element={<PropertyOwnersUsers />} />
              <Route path="users/team" element={<TeamAdmin />} />
              <Route path="users/roles" element={<RolesPermissions />} />
              <Route path="feature-controls" element={<FeatureControls />} />
              <Route path="seo" element={<SeoManager />} />
              <Route path="aeo" element={<AeoSchema />} />
              <Route path="sitemap" element={<SitemapIndexing />} />
              <Route path="redirects" element={<RedirectManager />} />
              <Route path="page-analytics" element={<PageAnalytics />} />
              <Route path="broken-links" element={<BrokenLinks />} />
              <Route path="search-performance" element={<SearchPerformance />} />
              <Route path="analytics-reports" element={<AnalyticsReports />} />
            </Route>
            <Route path="/admin/visual-editor/:pageKey" element={<VisualEditor />} />
            <Route path="/admin/about-us/edit" element={<Navigate to="/admin/visual-editor/about" replace />} />
            <Route path="*" element={<SeoLandingPage />} />
          </Routes>
          </Suspense>
          <Toaster position="top-center" richColors />
         </VirtualTourProvider>
        </LeadProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
